import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Dropdown } from "flowbite-react";
import { Spinner } from "../../../atoms/Spinner";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { FaLock } from "react-icons/fa6";
import { HiLockClosed } from "react-icons/hi";
import {
  useRolesCatalog,
  useRolesList,
  useUpsertRole,
} from "../../../../hooks/roles";
import { useGetRoleList } from "../../../../hooks/users";
import {
  RbacLevel,
  RoleMatrix,
  setLevel,
  levelRank,
  capabilityGrantLevels,
  Cap,
  Level,
} from "../../../../utils/rbacCatalog";
import { usePermissions, PlanTier } from "../../../../utils/permissions";
import useAuth from "../../../../hooks/useAuth";
import { CustomButton } from "../../../atoms/CustomButton";
import FeatureUpgradePrompt from "../../../atoms/FeatureUpgradePrompt";
import { PlansModal } from "../../../modals/PlansModal";
import { UserRole } from "../../../../types/UserRolesTypes";

const RESERVED = new Set(["LOYALTY_PROG_USER"]);

const LOCKED_ROLES = new Set(["OWNER"]);

const normalizeStoredMatrix = (stored: RoleMatrix): RoleMatrix => {
  const out: RoleMatrix = {};
  for (const [cap, level] of Object.entries(stored || {})) {
    const offered = capabilityGrantLevels(cap);
    if (offered.includes(level as RbacLevel)) {
      out[cap] = level as RbacLevel;
      continue;
    }
    const covered = [...offered]
      .reverse()
      .find((offeredLevel) => levelRank(offeredLevel) <= levelRank(level));
    if (covered) out[cap] = covered;
  }
  return out;
};

const RoleMatrixEditor = () => {
  const [t] = useTranslation();
  const { user } = useAuth();
  const permissions = usePermissions(user);

  const canManageRoles = permissions.canManageRoles();
  const hasRoleViewCapability = permissions.can(
    Cap.ROLE_MANAGEMENT,
    Level.VIEW
  );
  const canView = canManageRoles && hasRoleViewCapability;
  const canWrite =
    canManageRoles && permissions.can(Cap.ROLE_MANAGEMENT, Level.WRITE);

  const { data: catalog, isLoading: catalogLoading } = useRolesCatalog(canView);
  const { data: roles, isLoading: rolesLoading } = useRolesList(canView);
  const { data: entityRoles, isLoading: entityRolesLoading } = useGetRoleList({
    user_identification: user.user_identification,
    entity_identification: user.entity_identification,
    enabled: canView,
  });
  const upsert = useUpsertRole();

  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [matrix, setMatrix] = useState<RoleMatrix>({});
  const [showPlansModal, setShowPlansModal] = useState(false);

  const callerRole = String(user?.user_role || "").toUpperCase();
  const isOwnRole =
    permissions.isRoleGoverned() &&
    !!selectedRole &&
    selectedRole.toUpperCase() === callerRole;

  const isLocked = selectedRole ? LOCKED_ROLES.has(selectedRole) : false;
  const canEditSelected = canWrite && !isLocked && !isOwnRole;

  const roleOptions = useMemo(() => {
    const names = new Set<string>();
    (entityRoles as UserRole[] | undefined)?.forEach((r) => {
      if (r?.user_role) names.add(r.user_role.toUpperCase());
    });
    (roles || []).forEach((r) => names.add(r.role.toUpperCase()));
    return Array.from(names)
      .filter((name) => !RESERVED.has(name))
      .sort();
  }, [entityRoles, roles]);

  const loadedRoleRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedRole || !roles) return;
    if (loadedRoleRef.current === selectedRole) return;
    loadedRoleRef.current = selectedRole;
    const found = roles.find((r) => r.role === selectedRole);
    setMatrix(found ? normalizeStoredMatrix(found.permissions) : {});
  }, [selectedRole, roles]);

  useEffect(() => {
    if (!selectedRole && roleOptions.length > 0) {
      setSelectedRole(roleOptions[0]);
    }
  }, [roleOptions, selectedRole]);

  const levelOf = (cap: string): RbacLevel =>
    (matrix[cap] as RbacLevel) || "none";

  const chooseLevel = (cap: string, level: RbacLevel) => {
    setMatrix((prev) => setLevel(prev, cap, level));
  };

  const handleSave = () => {
    if (!selectedRole || !canEditSelected) return;
    upsert.mutate(
      { role: selectedRole, permissions: matrix },
      {
        onSuccess: () => {
          toast.success(t("roleEditor.saved"));
        },
        onError: (e: any) => {
          toast.error(e?.response?.data?.message || t("roleEditor.saveFailed"));
        },
      }
    );
  };

  const capabilityLabel = (capKey: string) => {
    const title = t(`roleEditor.cap.${capKey}.title`);
    const desc = t(`roleEditor.cap.${capKey}.desc`);
    return (
      <div className="flex flex-col gap-0.5">
        <span className="text-gray-800 font-medium">{title}</span>
        {desc && (
          <span className="text-gray-500 text-xs leading-snug">{desc}</span>
        )}
      </div>
    );
  };

  if (!canView) {
    return hasRoleViewCapability ? (
      <>
        <FeatureUpgradePrompt
          icon={HiLockClosed}
          iconTheme="blue"
          title={t("roleEditor.lockedTitle") as string}
          description={t("roleEditor.lockedDesc") as string}
          benefitObjects={[
            {
              id: "roles-custom",
              text: t("roleEditor.lockedBenefitRoles") as string,
            },
            {
              id: "roles-scope",
              text: t("roleEditor.lockedBenefitScope") as string,
            },
            {
              id: "roles-delegate",
              text: t("roleEditor.lockedBenefitDelegate") as string,
            },
          ]}
          upgradeText={t("roleEditor.lockedUpgrade") as string}
          onUpgrade={() => setShowPlansModal(true)}
          className="w-full"
        />
        <PlansModal
          showPlansModal={showPlansModal}
          setShowPlansModal={setShowPlansModal}
          targetPlan={PlanTier.PREMIUM}
        />
      </>
    ) : null;
  }

  if (catalogLoading || rolesLoading || entityRolesLoading) {
    return (
      <div className="flex w-full items-center gap-3 rounded-lg border border-gray-200 p-4 text-sm text-gray-500">
        <Spinner size="sm" /> <span>{t("roleEditor.loading")}</span>
      </div>
    );
  }

  const levelSelector = (cap: string, maxLevel: RbacLevel) => {
    const current = isLocked ? maxLevel : levelOf(cap);
    const steps: RbacLevel[] = ["none", ...capabilityGrantLevels(cap)];
    return (
      <div className="inline-flex rounded-md border border-gray-300 overflow-hidden divide-x divide-gray-300 w-full sm:w-fit">
        {steps.map((step) => {
          const active =
            step === "none"
              ? current === "none"
              : levelRank(step) <= levelRank(current);
          const label = t(`roleEditor.level.${step}`) as string;
          return (
            <button
              key={step}
              type="button"
              disabled={!canEditSelected}
              onClick={() => canEditSelected && chooseLevel(cap, step)}
              className={`flex-1 sm:flex-none min-w-0 px-2 sm:px-3 py-1.5 text-xs font-medium truncate transition-colors ${
                active
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              } ${
                canEditSelected
                  ? "cursor-pointer"
                  : "cursor-not-allowed opacity-70"
              }`}
              aria-pressed={active}
              aria-label={`${cap} ${step}`}
              title={label}
            >
              {label}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <p className="text-sm text-gray-500">{t("roleEditor.subtitle")}</p>

      {roleOptions.length === 0 ? (
        <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
          {t("roleEditor.noRolesHint")}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <Dropdown
            label={selectedRole || t("roleEditor.selectRole")}
            color="light"
          >
            {roleOptions.map((r) => (
              <Dropdown.Item key={r} onClick={() => setSelectedRole(r)}>
                {LOCKED_ROLES.has(r) ? (
                  <span className="flex items-center gap-1.5">
                    <FaLock size={12} className="text-amber-500 shrink-0" />
                    {r}
                  </span>
                ) : (
                  r
                )}
              </Dropdown.Item>
            ))}
          </Dropdown>
        </div>
      )}

      {isLocked && (
        <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5">
          <FaLock size={13} className="shrink-0" />
          <span>{t("roleEditor.lockedNotice", { role: selectedRole })}</span>
        </div>
      )}

      {isOwnRole && !isLocked && (
        <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5">
          <FaLock size={13} className="shrink-0" />
          <span>{t("roleEditor.ownRoleNotice")}</span>
        </div>
      )}

      {selectedRole && catalog?.groups && (
        <>
          <div className="hidden md:block overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">
                    {t("roleEditor.capability")}
                  </th>
                  <th className="text-left px-4 py-3 font-semibold">
                    {t("roleEditor.level.header")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {catalog.groups.map((group) => (
                  <Fragment key={`g-${group.group}`}>
                    <tr className="border-t border-gray-200 bg-gray-50">
                      <td
                        colSpan={2}
                        className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500"
                      >
                        {t(`roleEditor.group.${group.group}`)}
                      </td>
                    </tr>
                    {group.capabilities.map((capDef) => (
                      <tr key={capDef.key} className="border-t border-gray-100">
                        <td className="px-4 py-2.5 text-gray-700 align-top max-w-md">
                          {capabilityLabel(capDef.key)}
                        </td>
                        <td className="px-4 py-2.5 align-top">
                          {levelSelector(capDef.key, capDef.maxLevel)}
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden flex flex-col gap-4">
            {catalog.groups.map((group) => (
              <div key={`gm-${group.group}`} className="flex flex-col gap-2">
                <div className="px-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {t(`roleEditor.group.${group.group}`)}
                </div>
                <div className="flex flex-col gap-2">
                  {group.capabilities.map((capDef) => (
                    <div
                      key={capDef.key}
                      className="flex flex-col gap-2 border border-gray-200 rounded-lg p-3 bg-white"
                    >
                      <div className="text-sm">
                        {capabilityLabel(capDef.key)}
                      </div>
                      {levelSelector(capDef.key, capDef.maxLevel)}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {selectedRole && canEditSelected && catalog?.groups && (
        <div className="flex justify-end">
          <CustomButton
            variant="primary"
            icon="save"
            onClick={handleSave}
            isLoading={upsert.isLoading}
            disabled={upsert.isLoading}
            title={t("common.saveShort")}
          />
        </div>
      )}
    </div>
  );
};

export default RoleMatrixEditor;
